/**
 * Migrazioni schema — D48 (`schema_version` su ogni evento + registro migrazioni; pratica
 * Muse/Evolu, build plan §3 M2: "prima migrazione di schema testata").
 *
 * MODELLO: upcast in LETTURA. Il log è append-only (§2.3: gli eventi non si riscrivono MAI),
 * quindi la migrazione è una funzione pura che normalizza gli eventi quando vengono letti
 * (import, list, proiezione): i vecchi formati diventano il formato corrente SENZA toccare il
 * file. Idempotente: migrare due volte = migrare una.
 *
 * REGISTRO:
 *   v0 → v1 (D48, 2026-09-24): eventi PRE-D48 senza `schema_version`/`device_id` → completati.
 *   (la v1 prossima si aggiunge qui con una funzione per gradino, mai un if dentro l'app)
 *
 * NORMALIZZAZIONI DI FORMA (indipendenti dalla versione — servono agli eventi reali del device
 * scritti PRIMA dei fix del 24/09, "eventi demo PRE-fix"):
 *   - `set.created`/`set.updated` SENZA `session_id` top-level (§2.2 lo richiede) → derivato
 *     dal contesto: la sessione più recente in ordine totale D49 PRIMA dell'evento. Se non c'è
 *     nessuna sessione, l'evento resta orfano e il resoconto lo dice (mai un ID inventato).
 *     Il contesto è il LOG UNITO, non il solo batch: `contesto` = eventi già noti (log locale)
 *     che fanno da sfondo ma NON vengono emessi né contati (blind review M2 S3: con il solo
 *     batch, uno export spezzato in due file agganciava il set alla sessione sbagliata e
 *     PERSISTEVA l'errore).
 */
import { compareEvents, SCHEMA_VERSION, type HyprEvent } from "./events";
import { t } from "../i18n/it";

export interface ResocontoMigrazione {
  daV0: number; // eventi completati con schema_version/device_id
  setOrfani: number; // set senza session_id ricollegati alla sessione del contesto
  orfaniIrrisolti: number; // set senza sessione alcuna: restano orfani (dichiarato)
  placeholderNome: number; // `display_name: "demo"` PRE-D54 rimpiazzato col default D54
}

/**
 * Upcast in lettura di `eventi`. `contesto` (opzionale): eventi già noti che fanno da sfondo
 * all'ordine D49 senza essere emessi né contati nel resoconto — tipicamente il log locale,
 * quando si migra il solo batch importato.
 */
export function migraEventi(
  eventi: HyprEvent[],
  contesto: HyprEvent[] = [],
): { eventi: HyprEvent[]; resoconto: ResocontoMigrazione } {
  type Voce = { e: HyprEvent; emesso: boolean };
  const ordinati: Voce[] = [
    ...contesto.map((e): Voce => ({ e, emesso: false })),
    ...eventi.map((e): Voce => ({ e, emesso: true })),
  ].sort((a, b) => compareEvents(a.e, b.e));
  const resoconto: ResocontoMigrazione = { daV0: 0, setOrfani: 0, orfaniIrrisolti: 0, placeholderNome: 0 };
  let ultimaSessione: string | null = null;
  const out: HyprEvent[] = [];
  for (const voce of ordinati) {
    let m: HyprEvent = voce.e;
    // v0 → v1: i campi D48 mancanti si completano (mai riscritti nel file: solo in lettura)
    if (typeof m.schema_version !== "number") {
      m = { ...m, schema_version: SCHEMA_VERSION };
      if (voce.emesso) resoconto.daV0 += 1;
    }
    // placeholder PRE-D54 (D54, 2026-09-26): `display_name: "demo"` in `user.created` lo abbiamo
    // scritto NOI prima che esistesse un default onesto — non è un dato dell'utente. In lettura
    // diventa il default D54: mai valori imbarazzanti nell'UI o negli export (segnalazione
    // device 2026-09-26: "nel campo Nome appare demo").
    if (m.type === "user.created" && m.data["display_name"] === "demo") {
      m = { ...m, data: { ...m.data, display_name: t("defaultDisplayName") } };
      if (voce.emesso) resoconto.placeholderNome += 1;
    }
    // contesto sessione: ogni evento che porta un session_id aggiorna il contesto
    const s = typeof m["session_id"] === "string" ? (m["session_id"] as string) : null;
    if (m.type === "session.created" && s !== null) ultimaSessione = s;
    // normalizzazione di forma: set senza session_id top-level → quello del contesto
    if ((m.type === "set.created" || m.type === "set.updated") && s === null) {
      if (ultimaSessione !== null) {
        m = { ...m, session_id: ultimaSessione };
        if (voce.emesso) resoconto.setOrfani += 1;
      } else if (voce.emesso) {
        resoconto.orfaniIrrisolti += 1;
      }
    }
    if (m["session_id"] !== undefined && m["session_id"] !== null && typeof m["session_id"] === "string") {
      ultimaSessione = m["session_id"] as string;
    }
    if (voce.emesso) out.push(m);
  }
  return { eventi: out, resoconto };
}
